import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Download, ArrowRight, Edit2, X, Upload, FileCode2, Loader2, Euro, Banknote, Landmark, Undo2, FileSignature, Package } from "lucide-react";
import PageHelp from "@/components/PageHelp";

const STATUT_COLORS = {
  brouillon: "bg-slate-100 text-slate-700",
  envoye: "bg-blue-100 text-blue-700",
  accepte: "bg-emerald-100 text-emerald-700",
  refuse: "bg-red-100 text-red-700",
  converti: "bg-indigo-100 text-indigo-700",
};
const STATUT_LABELS = { brouillon: "Brouillon", envoye: "Envoyé", accepte: "Accepté", refuse: "Refusé", converti: "Converti" };

const TVA_RATES = [
  { value: "0.21", label: "21%" },
  { value: "0.12", label: "12%" },
  { value: "0.06", label: "6%" },
  { value: "0", label: "0%" },
];

const emptyLigne = () => ({ description: "", quantite: 1, prix_unitaire: 0, taux_tva: 0.21 });
const emptyForm = () => ({
  type_document: "devis",
  client_nom: "", client_telephone: "", client_email: "", client_tva: "",
  client_adresse: "", client_ville: "", client_code_postal: "", client_pays: "BE",
  numero_commande_po: "",
  lignes: [emptyLigne()], tva_rate: 0.21, notes: "",
  date_validite: "", date_echeance_paiement: "",
  mode_paiement: "", date_paiement: "",
});

const Devis = () => {
  const { token } = useAuth();
  const [devisList, setDevisList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDevis, setEditingDevis] = useState(null);
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterType, setFilterType] = useState("all"); // all | devis | facture
  const [form, setForm] = useState(emptyForm());
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  // Paiement dialog
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payTargetDevis, setPayTargetDevis] = useState(null);
  const [payMode, setPayMode] = useState("liquide");
  const [payIban, setPayIban] = useState("");
  const [vendorIban, setVendorIban] = useState("");
  const [creatingVente, setCreatingVente] = useState(false);
  // Bulk PDF selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const [produits, setProduits] = useState([]);
  // Lier les articles au stock
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTargetDevis, setLinkTargetDevis] = useState(null);
  const [linkLignes, setLinkLignes] = useState([]);
  const [savingLinks, setSavingLinks] = useState(false);

  useEffect(() => {
    axios.get(`${API}/produits`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setProduits(res.data)).catch(() => {});
  }, []); // eslint-disable-line

  const pickProduit = (idx, sku) => {
    const p = produits.find(x => x.sku === sku);
    if (!p) return;
    const lignes = [...form.lignes];
    lignes[idx] = { ...lignes[idx], description: `Chaise ${p.modele} ${p.couleur}`, prix_unitaire: p.prix_b2b_ht || p.prix_b2c_ttc || 0, sku: p.sku };
    setForm({ ...form, lignes });
  };

  // Auto-détection d'un SKU depuis une description libre
  const FRAIS_WORDS = ["livraison", "frais", "remise", "réduction", "reduction", "transport", "montage", "acompte"];
  const COULEUR_MAP = { black: "noir", noir: "noir", noire: "noir", white: "blanc", blanc: "blanc", blanche: "blanc", sand: "beige", beige: "beige", grey: "gris", gray: "gris", gris: "gris", grise: "gris", pink: "rose", rose: "rose", transparent: "transparent", transparente: "transparent", clear: "transparent" };
  const autoDetectSku = (description) => {
    const words = (description || "").toLowerCase().match(/[a-zA-Zéèêëàâîïôùûç]+/g) || [];
    if (words.some(w => FRAIS_WORDS.includes(w))) return "__FRAIS__";
    const modeles = new Set(produits.map(p => (p.modele || "").toLowerCase().trim()));
    const modele = words.find(w => modeles.has(w));
    const couleur = words.map(w => COULEUR_MAP[w]).find(Boolean);
    if (!modele || !couleur) return "";
    const p = produits.find(x => (x.modele || "").toLowerCase().trim() === modele && (x.couleur || "").toLowerCase().trim() === couleur);
    return p?.sku || "";
  };

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDevis = async () => {
    try {
      const q = filterStatut !== "all" ? `?statut=${filterStatut}` : "";
      const res = await axios.get(`${API}/devis${q}`, { headers });
      setDevisList(res.data);
    } catch { toast.error("Erreur chargement devis"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDevis(); }, [filterStatut]); // eslint-disable-line

  // Load vendor IBAN once (for virement default)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/parametres`, { headers });
        setVendorIban(res.data?.vendeur_iban || "");
      } catch { /* ignore */ }
    })();
  }, []); // eslint-disable-line

  const openPayDialog = (devis) => {
    setPayTargetDevis(devis);
    setPayMode("liquide");
    setPayIban(vendorIban);
    setPayDialogOpen(true);
  };

  const confirmCreateVente = async () => {
    if (!payTargetDevis) return;
    setCreatingVente(true);
    try {
      const res = await axios.post(
        `${API}/devis/${payTargetDevis.id}/create-vente`,
        {
          mode_paiement: payMode,
          iban_recu: payMode === "virement" ? (payIban || "") : "",
        },
        { headers }
      );
      toast.success(res.data.message || "Ventes créées !");
      setPayDialogOpen(false);
      setPayTargetDevis(null);
      fetchDevis();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur création ventes");
    } finally {
      setCreatingVente(false);
    }
  };

  const addLigne = () => setForm({ ...form, lignes: [...form.lignes, emptyLigne()] });
  const removeLigne = (idx) => setForm({ ...form, lignes: form.lignes.filter((_, i) => i !== idx) });
  const updateLigne = (idx, field, value) => {
    const lignes = [...form.lignes];
    lignes[idx] = { ...lignes[idx], [field]: field === "description" ? value : Number(value) };
    setForm({ ...form, lignes });
  };

  // Totals with per-line TVA
  const totalHT = form.lignes.reduce((a, l) => a + (l.quantite || 0) * (l.prix_unitaire || 0), 0);
  const tvaMontant = form.lignes.reduce((a, l) => {
    const lineHT = (l.quantite || 0) * (l.prix_unitaire || 0);
    const rate = l.taux_tva != null ? l.taux_tva : form.tva_rate;
    return a + lineHT * rate;
  }, 0);
  const totalTTC = totalHT + tvaMontant;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        lignes: form.lignes.map(l => ({ ...l, total: (l.quantite || 0) * (l.prix_unitaire || 0) })),
      };
      if (editingDevis) {
        await axios.put(`${API}/devis/${editingDevis.id}`, payload, { headers });
        toast.success("Devis mis à jour !");
      } else {
        await axios.post(`${API}/devis`, payload, { headers });
        toast.success("Devis créé !");
      }
      setDialogOpen(false);
      resetForm();
      fetchDevis();
    } catch { toast.error("Erreur"); }
  };

  const resetForm = () => {
    setEditingDevis(null);
    setForm(emptyForm());
  };

  const openEdit = (d) => {
    setEditingDevis(d);
    setForm({
      type_document: d.type_document || "devis",
      client_nom: d.client_nom, client_telephone: d.client_telephone || "", client_email: d.client_email || "",
      client_tva: d.client_tva || "", client_adresse: d.client_adresse || "",
      client_ville: d.client_ville || "", client_code_postal: d.client_code_postal || "",
      client_pays: d.client_pays || "BE",
      numero_commande_po: d.numero_commande_po || "",
      lignes: d.lignes?.length
        ? d.lignes.map(l => ({ ...l, taux_tva: l.taux_tva != null ? l.taux_tva : (d.tva_rate || 0.21) }))
        : [emptyLigne()],
      tva_rate: d.tva_rate || 0.21,
      notes: d.notes || "",
      date_validite: d.date_validite || "",
      date_echeance_paiement: d.date_echeance_paiement || "",
      mode_paiement: d.mode_paiement || "",
      date_paiement: d.date_paiement || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce devis ?")) return;
    try { await axios.delete(`${API}/devis/${id}`, { headers }); toast.success("Devis supprimé"); fetchDevis(); }
    catch { toast.error("Erreur suppression"); }
  };

  const handleDownloadPDF = async (id, numero) => {
    try {
      const res = await axios.get(`${API}/devis/${id}/pdf`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `devis_${numero || id}.pdf`; a.click(); a.remove();
      toast.success("PDF téléchargé !");
    } catch { toast.error("Erreur PDF"); }
  };

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (ids) => {
    setSelectedIds(prev => {
      const allSelected = ids.every(i => prev.has(i));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach(i => next.delete(i));
        return next;
      }
      const next = new Set(prev);
      ids.forEach(i => next.add(i));
      return next;
    });
  };
  const handleBulkDownloadPDF = async () => {
    if (selectedIds.size === 0) return;
    setDownloadingBulk(true);
    try {
      const res = await axios.post(`${API}/devis/bulk-pdf`, { ids: Array.from(selectedIds) },
        { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `documents_${today}_${selectedIds.size}docs.pdf`;
      a.click(); a.remove();
      toast.success(`${selectedIds.size} document(s) téléchargés en 1 PDF !`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur téléchargement groupé");
    } finally {
      setDownloadingBulk(false);
    }
  };

  const handleDownloadXML = async (id, numero) => {
    try {
      const res = await axios.get(`${API}/devis/${id}/export-xml`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url; a.download = `devis_${numero || id}_peppol.xml`; a.click(); a.remove();
      toast.success("XML Peppol téléchargé !");
    } catch { toast.error("Erreur export XML"); }
  };

  const handleConvert = async (id) => {
    const d = devisList.find(x => x.id === id);
    if (d && !allLinesLinked(d.lignes)) {
      toast.warning("Certaines lignes ne sont pas liées au stock. Utilise le bouton 🔗 pour les lier d'abord.", { duration: 8000 });
      openLinkDialog(d);
      return;
    }
    if (!window.confirm("Convertir ce devis en livraison ?")) return;
    try {
      const res = await axios.post(`${API}/devis/${id}/convert`, {}, { headers });
      if (res.data.articles_lies > 0) {
        toast.success(`${res.data.message} — ${res.data.articles_lies} article(s) relié(s) au stock ✅`);
      } else {
        toast.warning("Livraison créée MAIS articles NON reliés au stock.", { duration: 10000 });
      }
      fetchDevis();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur conversion"); }
  };

  const handleUnconvert = async (id) => {
    if (!window.confirm("Annuler la conversion ?\n\nLa livraison associée sera déplacée vers la corbeille (restaurable 15 jours).")) return;
    try {
      const res = await axios.post(`${API}/devis/${id}/unconvert`, {}, { headers });
      toast.success(res.data.message || "Conversion annulée");
      fetchDevis();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openLinkDialog = (d) => {
    setLinkTargetDevis(d);
    const enriched = (d.lignes || []).map(l => ({
      ...l,
      sku: l.sku || autoDetectSku(l.description),
    }));
    setLinkLignes(enriched);
    setLinkDialogOpen(true);
  };

  const updateLinkLigneSku = (idx, sku) => {
    const arr = [...linkLignes];
    arr[idx] = { ...arr[idx], sku };
    setLinkLignes(arr);
  };

  const saveLinks = async () => {
    if (!linkTargetDevis) return;
    setSavingLinks(true);
    try {
      const newLignes = linkLignes.map(l => ({ ...l }));
      await axios.put(`${API}/devis/${linkTargetDevis.id}`, { lignes: newLignes }, { headers });
      toast.success("Liaisons enregistrées ✅");
      setLinkDialogOpen(false);
      fetchDevis();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur enregistrement");
    } finally { setSavingLinks(false); }
  };

  // Vérifie si toutes les lignes article ont un SKU (ou sont des frais)
  const allLinesLinked = (lignes) => {
    for (const l of lignes || []) {
      const auto = l.sku || autoDetectSku(l.description);
      if (!auto) return false;
    }
    return true;
  };

  const handleConvertToFacture = async (d) => {
    if (!window.confirm(`Convertir le devis ${d.numero} en facture ?\n\nUne nouvelle facture sera créée avec les mêmes informations.`)) return;
    try {
      const res = await axios.post(`${API}/devis/${d.id}/convert-to-facture`, {}, { headers });
      toast.success(res.data.message);
      fetchDevis();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleStatutChange = async (id, newStatut) => {
    try {
      await axios.put(`${API}/devis/${id}`, { statut: newStatut }, { headers });
      toast.success(`Statut changé en "${STATUT_LABELS[newStatut]}"`);
      fetchDevis();
    } catch { toast.error("Erreur changement statut"); }
  };

  const handleImportPDF = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Veuillez sélectionner un fichier PDF");
      return;
    }
    setImporting(true);
    const toastId = toast.loading("Analyse du PDF en cours (IA)…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/devis/import-pdf`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const parsed = res.data;
      // Pre-fill form from parsed data
      setEditingDevis(null);
      setForm({
        client_nom: parsed.client_nom || "",
        client_telephone: parsed.client_telephone || "",
        client_email: parsed.client_email || "",
        client_tva: parsed.client_tva || "",
        client_adresse: parsed.client_adresse || "",
        client_ville: parsed.client_ville || "",
        client_code_postal: parsed.client_code_postal || "",
        client_pays: parsed.client_pays || "BE",
        numero_commande_po: parsed.numero_commande_po || "",
        lignes: (parsed.lignes && parsed.lignes.length)
          ? parsed.lignes.map(l => ({
              description: l.description || "",
              quantite: Number(l.quantite) || 1,
              prix_unitaire: Number(l.prix_unitaire) || 0,
              taux_tva: Number(l.taux_tva) != null ? Number(l.taux_tva) : 0.21,
            }))
          : [emptyLigne()],
        tva_rate: 0.21,
        notes: parsed.notes || "",
        date_validite: "",
        date_echeance_paiement: parsed.date_echeance_paiement || "",
      });
      setDialogOpen(true);
      toast.success("PDF analysé — vérifie et ajuste avant de créer le devis", { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur analyse PDF", { id: toastId });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const allTotalTTC = devisList.reduce((a, d) => a + (d.total_ttc || 0), 0);
  const acceptes = devisList.filter(d => d.statut === "accepte");
  const enAttente = devisList.filter(d => ["brouillon", "envoye"].includes(d.statut));
  const filteredList = devisList.filter(d => {
    if (filterType === "all") return true;
    const t = d.type_document || "devis";
    return t === filterType;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="devis-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-slate-800">Devis & Factures</h1>
            <PageHelp title="Devis & Factures" emoji="📄">
              <p>Gestion unifiée de tes <b>devis</b> (propositions commerciales) et <b>factures</b> (documents comptables), conformes à la structure <b>Peppol BIS 3.0</b>.</p>
              <ul className="space-y-1.5 list-disc pl-5">
                <li><b>Nouveau</b> — choisis le type (Devis ou Facture) en haut du formulaire</li>
                <li><b>Numérotation auto</b> : <code>DEV-xxx</code> pour les devis, <code>FACT-xxx</code> pour les factures</li>
                <li><b>Convertir</b> — bouton <FileSignature className="w-3 h-3 inline" /> pour transformer un devis en facture (crée une nouvelle facture indépendante)</li>
                <li><b>Importer PDF / Image</b> — l'IA lit un fichier (PDF, photo) et pré-remplit le formulaire</li>
                <li><b>Export XML Peppol</b> — format UBL 2.1 conforme EN16931</li>
                <li><b>TVA par ligne</b> — taux différent possible par produit (6%, 12%, 21%, 0%)</li>
              </ul>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                ⚠️ <b>Important&nbsp;:</b> configure tes infos vendeur (TVA, IBAN, BIC) dans <b>Paramètres</b> pour qu'elles apparaissent sur les PDF et XML.
              </div>
            </PageHelp>
          </div>
          <p className="text-slate-500 mt-1">Devis + Factures Peppol-ready · import PDF IA · export XML UBL</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
            className="hidden"
            onChange={handleImportPDF}
            data-testid="import-pdf-input"
          />
          <Button
            variant="outline"
            className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-pdf-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importer PDF / Image
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-devis-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau document
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Devis</p><p className="text-2xl font-bold text-indigo-600">{devisList.filter(d => (d.type_document || 'devis') === 'devis').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Factures</p><p className="text-2xl font-bold text-emerald-600">{devisList.filter(d => d.type_document === 'facture').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Montant total TTC</p><p className="text-2xl font-bold text-slate-800">{allTotalTTC.toFixed(2)} €</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-slate-500">En attente</p><p className="text-2xl font-bold text-amber-600">{enAttente.length}</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex gap-2 flex-wrap">
            <span className="text-xs text-slate-500 self-center mr-1">Type :</span>
            {[["all", "Tous"], ["devis", "Devis"], ["facture", "Factures"]].map(([k, label]) => (
              <Button key={k} size="sm" variant={filterType === k ? "default" : "outline"}
                className={filterType === k ? "bg-indigo-600" : ""}
                onClick={() => setFilterType(k)} data-testid={`filter-type-${k}`}>
                {label}
              </Button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <span className="text-xs text-slate-500 self-center mr-1">Statut :</span>
            {["all", "brouillon", "envoye", "accepte", "refuse", "converti"].map(s => (
              <Button key={s} size="sm" variant={filterStatut === s ? "default" : "outline"}
                className={filterStatut === s ? "bg-indigo-600" : ""}
                onClick={() => setFilterStatut(s)} data-testid={`filter-${s}`}>
                {s === "all" ? "Tous" : STATUT_LABELS[s]}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Devis Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="devis-dialog">
          <DialogHeader><DialogTitle>{editingDevis ? `Modifier ${editingDevis.numero}` : "Nouveau document"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* Type document */}
            <div className="bg-slate-50 border rounded-lg p-3">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Type de document</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, type_document: "devis" })}
                  className={`border rounded-lg p-3 text-left transition-colors ${form.type_document === "devis" ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  data-testid="type-devis"
                >
                  <div className="flex items-center gap-2">
                    <FileText className={`w-5 h-5 ${form.type_document === "devis" ? "text-indigo-600" : "text-slate-400"}`} />
                    <span className="font-medium text-sm">Devis</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Proposition commerciale (numéro DEV-…)</p>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, type_document: "facture" })}
                  className={`border rounded-lg p-3 text-left transition-colors ${form.type_document === "facture" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  data-testid="type-facture"
                >
                  <div className="flex items-center gap-2">
                    <FileSignature className={`w-5 h-5 ${form.type_document === "facture" ? "text-emerald-600" : "text-slate-400"}`} />
                    <span className="font-medium text-sm">Facture</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Document comptable (numéro FACT-…)</p>
                </button>
              </div>
            </div>

            {/* Client */}
            <div className="space-y-3 border border-slate-200 rounded-lg p-3 bg-slate-50/50">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Nom / Raison sociale *</Label><Input value={form.client_nom} onChange={e => setForm({ ...form, client_nom: e.target.value })} required data-testid="devis-client-nom" /></div>
                <div className="space-y-1"><Label>N° TVA (Peppol)</Label><Input value={form.client_tva} onChange={e => setForm({ ...form, client_tva: e.target.value })} placeholder="BE0123.456.789" data-testid="devis-client-tva" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Téléphone</Label><Input value={form.client_telephone} onChange={e => setForm({ ...form, client_telephone: e.target.value })} data-testid="devis-client-tel" /></div>
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} data-testid="devis-client-email" /></div>
              </div>
              <div className="space-y-1"><Label>Adresse</Label><Input value={form.client_adresse} onChange={e => setForm({ ...form, client_adresse: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label>Code postal</Label><Input value={form.client_code_postal} onChange={e => setForm({ ...form, client_code_postal: e.target.value })} /></div>
                <div className="space-y-1"><Label>Ville</Label><Input value={form.client_ville} onChange={e => setForm({ ...form, client_ville: e.target.value })} /></div>
                <div className="space-y-1"><Label>Pays</Label><Input value={form.client_pays} onChange={e => setForm({ ...form, client_pays: e.target.value.toUpperCase() })} maxLength={2} placeholder="BE" /></div>
              </div>
            </div>

            {/* Commande / Dates */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>N° commande (PO)</Label><Input value={form.numero_commande_po} onChange={e => setForm({ ...form, numero_commande_po: e.target.value })} placeholder="PO-2026-001" data-testid="devis-po" /></div>
              <div className="space-y-1"><Label>Date de validité</Label><Input type="date" value={form.date_validite} onChange={e => setForm({ ...form, date_validite: e.target.value })} /></div>
              <div className="space-y-1"><Label>Échéance paiement</Label><Input type="date" value={form.date_echeance_paiement} onChange={e => setForm({ ...form, date_echeance_paiement: e.target.value })} data-testid="devis-echeance" /></div>
            </div>

            {/* Paiement — factures uniquement */}
            {form.type_document === "facture" && (
              <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/40 space-y-3">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Paiement</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, mode_paiement: "", date_paiement: "" })}
                    className={`border rounded-lg p-2 text-left transition-colors ${form.mode_paiement === "" ? "border-slate-400 bg-white" : "border-slate-200 hover:border-slate-300 bg-white/60"}`}
                    data-testid="fact-pay-none"
                  >
                    <span className="font-medium text-sm">Non payée</span>
                    <p className="text-xs text-slate-500 mt-0.5">IBAN affiché sur PDF</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, mode_paiement: "liquide", date_paiement: form.date_paiement || new Date().toISOString().slice(0, 10) })}
                    className={`border rounded-lg p-2 text-left transition-colors ${form.mode_paiement === "liquide" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                    data-testid="fact-pay-liquide"
                  >
                    <div className="flex items-center gap-1.5">
                      <Banknote className={`w-4 h-4 ${form.mode_paiement === "liquide" ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className="font-medium text-sm">Payée en espèces</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Cash reçu</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, mode_paiement: "virement", date_paiement: form.date_paiement || new Date().toISOString().slice(0, 10) })}
                    className={`border rounded-lg p-2 text-left transition-colors ${form.mode_paiement === "virement" ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                    data-testid="fact-pay-virement"
                  >
                    <div className="flex items-center gap-1.5">
                      <Landmark className={`w-4 h-4 ${form.mode_paiement === "virement" ? "text-indigo-600" : "text-slate-400"}`} />
                      <span className="font-medium text-sm">Payée par virement</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">Reçu sur compte</p>
                  </button>
                </div>
                {form.mode_paiement && (
                  <div className="space-y-1 max-w-xs">
                    <Label>Date de paiement</Label>
                    <Input type="date" value={form.date_paiement || ""} onChange={e => setForm({ ...form, date_paiement: e.target.value })} data-testid="fact-pay-date" />
                  </div>
                )}
                <p className="text-xs text-emerald-700">
                  Si la facture est marquée payée, le PDF affichera « <b>FACTURE ACQUITTÉE — Payée {form.mode_paiement === "virement" ? "par virement bancaire" : "en espèces"}</b> » avec la date, et masquera l'IBAN.
                </p>
              </div>
            )}

            {/* Lignes */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Lignes du devis</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLigne}><Plus className="w-3 h-3 mr-1" />Ligne</Button>
              </div>
              <div className="grid grid-cols-12 gap-1.5 text-xs font-medium text-slate-500 px-2">
                <div className="col-span-5">Description</div>
                <div className="col-span-1 text-center">Qté</div>
                <div className="col-span-2 text-center">P.U. HT</div>
                <div className="col-span-2 text-center">TVA</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1"></div>
              </div>
              {form.lignes.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`devis-ligne-${i}`}>
                  <div className="col-span-5 flex gap-1">
                    <Select value="" onValueChange={(v) => pickProduit(i, v)}>
                      <SelectTrigger className="w-9 px-1.5 shrink-0" title="Choisir un produit du stock" data-testid={`devis-produit-select-${i}`}>
                        <Package className="w-4 h-4 text-indigo-500" />
                      </SelectTrigger>
                      <SelectContent>
                        {produits.map(p => (
                          <SelectItem key={p.sku} value={p.sku}>{p.modele} {p.couleur} — {p.stock_restant ?? 0} en stock</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Description produit" value={l.description} onChange={e => updateLigne(i, "description", e.target.value)} data-testid={`devis-desc-${i}`} />
                  </div>
                  <div className="col-span-1"><Input type="number" min="1" value={l.quantite} onChange={e => updateLigne(i, "quantite", e.target.value)} data-testid={`devis-qty-${i}`} /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" min="0" value={l.prix_unitaire} onChange={e => updateLigne(i, "prix_unitaire", e.target.value)} data-testid={`devis-price-${i}`} /></div>
                  <div className="col-span-2">
                    <Select value={String(l.taux_tva != null ? l.taux_tva : 0.21)} onValueChange={v => updateLigne(i, "taux_tva", v)}>
                      <SelectTrigger data-testid={`devis-tva-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TVA_RATES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 text-right font-medium text-sm">{((l.quantite || 0) * (l.prix_unitaire || 0)).toFixed(2)}€</div>
                  <div className="col-span-1">
                    {form.lignes.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => removeLigne(i)} className="text-red-500 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>}
                  </div>
                </div>
              ))}
              <div className="border-t pt-3 space-y-1 text-right text-sm">
                <p>Total HT : <strong>{totalHT.toFixed(2)} €</strong></p>
                <p>TVA : <strong>{tvaMontant.toFixed(2)} €</strong></p>
                <p className="text-lg font-bold text-indigo-600">Total TTC : {totalTTC.toFixed(2)} €</p>
              </div>
            </div>
            <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-devis-btn">{editingDevis ? "Enregistrer" : (form.type_document === "facture" ? "Créer la facture" : "Créer le devis")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Devis Table */}
      <Card data-testid="devis-table-card">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" />Documents ({filteredList.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : filteredList.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><FileText className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Aucun document</p></div>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <div className="flex items-center justify-between gap-3 p-3 mb-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="bulk-bar">
                  <span className="text-sm font-medium text-indigo-700">
                    <b>{selectedIds.size}</b> document(s) sélectionné(s)
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-btn">
                      <X className="w-4 h-4 mr-1" />Tout désélectionner
                    </Button>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleBulkDownloadPDF} disabled={downloadingBulk} data-testid="bulk-download-btn">
                      {downloadingBulk ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Télécharger la sélection (1 PDF fusionné)
                    </Button>
                  </div>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={filteredList.length > 0 && filteredList.every(d => selectedIds.has(d.id))}
                        onChange={() => toggleSelectAll(filteredList.map(d => d.id))}
                        className="w-4 h-4 cursor-pointer"
                        data-testid="select-all-checkbox"
                        title="Tout sélectionner / désélectionner"
                      />
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>N°</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>TVA</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Total TTC</TableHead>
                    <TableHead className="w-56"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredList.map(d => {
                    const isFacture = d.type_document === "facture";
                    return (
                    <TableRow key={d.id} data-testid={`devis-row-${d.id}`} className={selectedIds.has(d.id) ? "bg-indigo-50/50" : ""}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(d.id)}
                          onChange={() => toggleSelect(d.id)}
                          className="w-4 h-4 cursor-pointer"
                          data-testid={`select-${d.id}`}
                        />
                      </TableCell>
                      <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isFacture ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>
                        {isFacture ? "Facture" : "Devis"}
                      </span>
                      {isFacture && d.mode_paiement === "liquide" && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-white" title={`Payée en espèces${d.date_paiement ? ' le ' + new Date(d.date_paiement).toLocaleDateString('fr-FR') : ''}`}>✓ Cash</span>
                      )}
                      {isFacture && d.mode_paiement === "virement" && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-600 text-white" title={`Payée par virement${d.date_paiement ? ' le ' + new Date(d.date_paiement).toLocaleDateString('fr-FR') : ''}`}>✓ Virement</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{d.numero}</TableCell>
                    <TableCell className="font-medium">{d.client_nom}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{d.client_tva || "—"}</TableCell>
                    <TableCell className="text-sm">{d.date_creation ? new Date(d.date_creation).toLocaleDateString('fr-FR') : ""}</TableCell>
                    <TableCell>
                      <Select value={d.statut} onValueChange={(v) => handleStatutChange(d.id, v)} disabled={d.statut === "converti"}>
                        <SelectTrigger className={`w-32 h-8 text-xs font-medium ${STATUT_COLORS[d.statut]}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUT_LABELS).filter(([k]) => k !== "converti").map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right font-bold">{(d.total_ttc || 0).toFixed(2)} €</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-devis-${d.id}`} title="Modifier"><Edit2 className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadPDF(d.id, d.numero)} data-testid={`pdf-devis-${d.id}`} title="Télécharger PDF"><Download className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadXML(d.id, d.numero)} data-testid={`xml-devis-${d.id}`} title="Export XML Peppol" className="text-amber-600"><FileCode2 className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => openPayDialog(d)} data-testid={`create-vente-${d.id}`} title="Créer la vente" className="text-emerald-600"><Euro className="w-4 h-4" /></Button>
                        {!isFacture && (
                          <Button size="sm" variant="ghost" className="text-teal-600" onClick={() => handleConvertToFacture(d)} data-testid={`to-facture-${d.id}`} title="Convertir ce devis en facture"><FileSignature className="w-4 h-4" /></Button>
                        )}
                        {!d.livraison_id && d.statut !== "refuse" && (
                          <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => openLinkDialog(d)} data-testid={`link-devis-${d.id}`} title="Lier les articles au stock"><Package className="w-4 h-4" /></Button>
                        )}
                        {!d.livraison_id && d.statut !== "refuse" && (
                          <Button size="sm" variant="ghost" className="text-indigo-600" onClick={() => handleConvert(d.id)} data-testid={`convert-devis-${d.id}`} title="Convertir en livraison"><ArrowRight className="w-4 h-4" /></Button>
                        )}
                        {d.statut === "converti" && d.livraison_id && (
                          <Button size="sm" variant="ghost" className="text-orange-600" onClick={() => handleUnconvert(d.id)} data-testid={`unconvert-devis-${d.id}`} title="Annuler la conversion (livraison vers corbeille)"><Undo2 className="w-4 h-4" /></Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(d.id)} data-testid={`delete-devis-${d.id}`} title="Supprimer"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
      {/* Dialog paiement — Créer la vente */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-w-md" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Créer la vente — {payTargetDevis?.numero}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <div>
              <p className="text-sm text-slate-600 mb-2">
                <b>{payTargetDevis?.client_nom}</b> — {(payTargetDevis?.total_ttc || 0).toFixed(2)} € TTC
              </p>
              <p className="text-xs text-slate-400">
                Une ligne de vente sera créée pour chaque produit du devis. Le stock sera déduit si le modèle est reconnu.
              </p>
            </div>

            <div>
              <Label className="mb-2 block">Mode de paiement</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setPayMode("liquide")}
                  className={`border rounded-lg p-3 text-left transition-colors ${payMode === "liquide" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="pay-mode-liquide"
                >
                  <div className="flex items-center gap-2">
                    <Banknote className={`w-5 h-5 ${payMode === "liquide" ? "text-emerald-600" : "text-slate-400"}`} />
                    <span className="font-medium text-sm">Liquide</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Cash reçu</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPayMode("virement")}
                  className={`border rounded-lg p-3 text-left transition-colors ${payMode === "virement" ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="pay-mode-virement"
                >
                  <div className="flex items-center gap-2">
                    <Landmark className={`w-5 h-5 ${payMode === "virement" ? "text-indigo-600" : "text-slate-400"}`} />
                    <span className="font-medium text-sm">Virement</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Reçu par banque</p>
                </button>
              </div>
            </div>

            {payMode === "virement" && (
              <div>
                <Label>IBAN ayant reçu le paiement</Label>
                <Input
                  value={payIban}
                  onChange={(e) => setPayIban(e.target.value)}
                  placeholder={vendorIban || "BE68 5390 0754 7034"}
                  data-testid="pay-iban"
                />
                {!vendorIban && (
                  <p className="text-xs text-amber-600 mt-1">
                    Astuce : configure ton IBAN dans Paramètres &gt; Informations Peppol pour qu'il soit pré-rempli.
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setPayDialogOpen(false)} disabled={creatingVente}>
                Annuler
              </Button>
              <Button
                onClick={confirmCreateVente}
                disabled={creatingVente}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-create-vente"
              >
                {creatingVente ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Euro className="w-4 h-4 mr-2" />}
                Créer la vente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog "Lier les articles au stock" */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="link-dialog">
          <DialogHeader>
            <DialogTitle>
              🔗 Lier les articles au stock — {linkTargetDevis?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3">
              Sélectionne le produit correspondant à chaque ligne. Les lignes non liées <b>ne réserveront pas de stock</b> lors de la conversion en livraison.
            </div>
            {linkLignes.map((l, i) => {
              const isFrais = l.sku === "__FRAIS__";
              const linked = l.sku && l.sku !== "__FRAIS__" && produits.some(p => p.sku === l.sku);
              return (
                <div key={i} className={`border rounded-lg p-3 ${linked ? "border-emerald-300 bg-emerald-50" : isFrais ? "border-slate-300 bg-slate-50" : "border-orange-300 bg-orange-50"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-xs text-slate-500 mb-1">Ligne {i + 1} — Qté {l.quantite}</div>
                      <div className="font-medium text-sm">{l.description || "(vide)"}</div>
                    </div>
                    <div className="w-64 shrink-0">
                      <Select value={l.sku || ""} onValueChange={(v) => updateLinkLigneSku(i, v)}>
                        <SelectTrigger data-testid={`link-select-${i}`}>
                          <SelectValue placeholder="— Sélectionner —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__FRAIS__">🚚 Frais / Livraison / Remise</SelectItem>
                          {produits.map(p => (
                            <SelectItem key={p.sku} value={p.sku}>
                              {p.modele} — {p.couleur} ({p.sku})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {linked && <div className="text-xs text-emerald-700 mt-1">✅ Lié au stock</div>}
                      {isFrais && <div className="text-xs text-slate-600 mt-1">— Ignoré du stock</div>}
                      {!linked && !isFrais && <div className="text-xs text-orange-700 mt-1">⚠️ Non lié</div>}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="flex gap-2 justify-end pt-3 border-t">
              <Button variant="outline" onClick={() => setLinkDialogOpen(false)} disabled={savingLinks}>Annuler</Button>
              <Button onClick={saveLinks} disabled={savingLinks} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-links-btn">
                {savingLinks ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
                Enregistrer les liaisons
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Devis;
